// pages/SearchResultsPage.jsx
// UPDATED Feb 24, 2026: Fixed to use GET_ALL_PRODUCTS with client-side filtering
// (GET_ALL_PRODUCTS query doesn't exist in backend)
// ============================================

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ArrowLeft, MapPin, Filter, ChevronDown, ChevronUp, Search, ShoppingCart, Star, Loader } from 'lucide-react';
import SearchBar from '../components/common/SearchBar';
import BottomNav from '../components/common/BottomNav';
import { ProductCard } from '../components/products';
import { GET_ALL_PRODUCTS } from '../apollo/queries';
import { useCart } from '../context/CartContext';

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [distance, setDistance] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Extract search query from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    const zip = params.get('zip') || '';
    const cat = params.get('category') || 'all';
    setSearchTerm(q);
    setZipCode(zip);
    setCategoryFilter(cat);
  }, [location.search]);
  
  // Build filters object for GraphQL query
  const buildFilters = () => {
    const filters = {};
    
    if (categoryFilter && categoryFilter !== 'all') {
      filters.category = categoryFilter;
    }
    
    // Add price range if needed
    // filters.minPrice = 0;
    // filters.maxPrice = 1000;
    
    // Add availability filter
    filters.available = true;
    
    return Object.keys(filters).length > 0 ? filters : null;
  };
  
  // Fetch REAL products from backend using GET_ALL_PRODUCTS query
  const { 
    data: searchData, 
    loading: searchLoading, 
    error: searchError,
    refetch 
  } = useQuery(GET_ALL_PRODUCTS, {
    variables: { 
      category: categoryFilter !== "all" ? categoryFilter : null,
      limit: 200
    },
    skip: !searchTerm || searchTerm.length < 2, // Don't search for very short terms
    fetchPolicy: 'cache-and-network'
  });
  
  // Transform and sort results
  const results = React.useMemo(() => {
    if (!searchData?.products) return [];
    
    // Filter by search term (client-side search)
    const searchLower = searchTerm.toLowerCase().trim();
    const filteredProducts = searchData.products.filter(product => {
      if (!product) return false;
      const nameMatch = product.name?.toLowerCase().includes(searchLower);
      const descMatch = product.description?.toLowerCase().includes(searchLower);
      const categoryMatch = product.category?.toLowerCase().includes(searchLower);
      const vendorMatch = product.vendor?.name?.toLowerCase().includes(searchLower);
      return nameMatch || descMatch || categoryMatch || vendorMatch;
    });
    
    let products = filteredProducts.map(product => ({
      id: product.id,
      title: product.name,
      name: product.name,
      description: product.description,
      category: product.category,
      images: product.images || [],
      image: product.images?.[0] || '/placeholder-product.jpg',
      currentPrice: product.currentPrice || product.basePrice,
      originalPrice: product.basePrice,
      basePrice: product.basePrice,
      savings: product.basePrice > 0 
        ? Math.round((1 - (product.currentPrice || product.basePrice) / product.basePrice) * 100) 
        : 0,
      unit: product.unit || 'each',
      vendor: product.vendor?.name || 'Local Vendor',
      location: product.vendor?.location || 'Sacramento, CA',
      organic: product.attributes?.organic || false,
      local: product.attributes?.local || false,
      sustainable: product.attributes?.sustainablySourced || false,
      available: true
    }));
    
    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        products.sort((a, b) => a.currentPrice - b.currentPrice);
        break;
      case 'price-high':
        products.sort((a, b) => b.currentPrice - a.currentPrice);
        break;
      case 'savings':
        products.sort((a, b) => b.savings - a.savings);
        break;
      case 'name':
        products.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // 'relevance' - keep backend order
        break;
    }
    
    return products;
  }, [searchData, sortBy, searchTerm]);

  const handleGoBack = () => {
    navigate(-1);
  };
  
  const handleZipCodeSubmit = (e) => {
    e.preventDefault();
    if (zipCode) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}&zip=${zipCode}`);
    }
  };
  
  const handleSortChange = (value) => {
    setSortBy(value);
  };
  
  const handleCategoryChange = (category) => {
    setCategoryFilter(category);
    const params = new URLSearchParams(location.search);
    if (category === 'all') {
      params.delete('category');
    } else {
      params.set('category', category);
    }
    navigate(`/search?${params.toString()}`);
  };
  
  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };
  
  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    addToCart({
      id: product.id,
      title: product.title || product.name,
      name: product.name,
      price: product.currentPrice,
      currentPrice: product.currentPrice,
      image: product.image,
      images: product.images,
      quantity: 1,
      unit: product.unit,
      vendor: product.vendor
    });
  };

  // Categories for filter
  const categories = [
    'all',
    'Groceries',
    'Household',
    'Personal Care',
    'Baby',
    'Electronics',
    'Home & Garden',
    'Health & Wellness'
  ];

  return (
    <div className="container mx-auto px-4 pb-20">
      <div className="sticky top-0 bg-white pt-4 pb-2 z-10">
        <div className="flex items-center mb-3">
          <button 
            className="mr-2 p-1 hover:bg-gray-100 rounded-full"
            onClick={handleGoBack}
          >
            <ArrowLeft size={20} />
          </button>
          <SearchBar 
            placeholder="Search products..." 
            className="flex-1"
            initialValue={searchTerm}
          />
        </div>
        
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-medium">
            {searchLoading ? (
              <span className="flex items-center gap-2">
                <Loader size={16} className="animate-spin" />
                Searching...
              </span>
            ) : searchTerm ? (
              `${results.length} result${results.length !== 1 ? 's' : ''} for "${searchTerm}"`
            ) : (
              'Enter a search term'
            )}
          </h2>
          <button 
            className="flex items-center text-blue-600"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} className="mr-1" />
            <span className="text-sm">Filters</span>
            {showFilters ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
          </button>
        </div>
        
        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-gray-50 rounded-lg p-4 mb-3">
            {/* Sort Options */}
            <div className="mb-4">
              <h3 className="font-medium mb-2">Sort By</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'relevance', label: 'Relevance' },
                  { value: 'price-low', label: 'Price: Low to High' },
                  { value: 'price-high', label: 'Price: High to Low' },
                  { value: 'savings', label: 'Best Savings' },
                  { value: 'name', label: 'Name A-Z' }
                ].map(option => (
                  <button 
                    key={option.value}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      sortBy === option.value 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    onClick={() => handleSortChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="mb-4">
              <h3 className="font-medium mb-2">Category</h3>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button 
                    key={cat}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      categoryFilter === cat 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    onClick={() => handleCategoryChange(cat)}
                  >
                    {cat === 'all' ? 'All Categories' : cat}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Location Filter */}
            <div>
              <h3 className="font-medium mb-2">Location</h3>
              <form onSubmit={handleZipCodeSubmit} className="flex mb-2">
                <div className="relative flex-1 mr-2">
                  <input 
                    type="text" 
                    placeholder="Enter ZIP code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg pr-8"
                  />
                  <MapPin size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
                <select 
                  value={distance}
                  onChange={(e) => setDistance(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="5">5 miles</option>
                  <option value="10">10 miles</option>
                  <option value="20">20 miles</option>
                  <option value="50">50 miles</option>
                </select>
              </form>
            </div>
          </div>
        )}
      </div>
      
      {/* Results */}
      {!searchTerm || searchTerm.length < 2 ? (
        <div className="text-center py-10 bg-white rounded-lg shadow mt-4">
          <div className="text-gray-400 mb-2">
            <Search size={48} className="mx-auto" />
          </div>
          <p className="text-gray-700 font-medium mb-2">Search for products</p>
          <p className="text-sm text-gray-500 max-w-xs mx-auto">
            Enter at least 2 characters to search our catalog of products.
          </p>
        </div>
      ) : searchLoading ? (
        <div className="flex justify-center items-center py-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-gray-600">Searching for "{searchTerm}"...</p>
          </div>
        </div>
      ) : searchError ? (
        <div className="text-center py-10 bg-red-50 rounded-lg mt-4">
          <p className="text-red-600 font-medium mb-2">Error searching products</p>
          <p className="text-sm text-red-500 mb-4">{searchError.message}</p>
          <button 
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      ) : results.length > 0 ? (
        <div className="mt-2">
          {/* Grid View */}
          <div className="grid grid-cols-2 gap-3">
            {results.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                compact={true}
                onClick={() => handleProductClick(product.id)}
              />
            ))}
          </div>
          
          {/* Results count footer */}
          <div className="text-center py-4 text-sm text-gray-500">
            Showing {results.length} product{results.length !== 1 ? 's' : ''}
          </div>
        </div>
      ) : (
        <div className="text-center py-10 bg-white rounded-lg shadow mt-4">
          <div className="text-gray-400 mb-2">
            <Search size={48} className="mx-auto" />
          </div>
          <p className="text-gray-700 font-medium mb-2">No results found for "{searchTerm}"</p>
          <p className="text-sm text-gray-500 max-w-xs mx-auto mb-4">
            Try different keywords, check your spelling, or browse by category to find what you're looking for.
          </p>
          
          {/* Suggestions */}
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-2">Try searching for:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {['Paper Towels', 'Dish Soap', 'Laundry', 'Snacks', 'Cleaning'].map(suggestion => (
                <button
                  key={suggestion}
                  onClick={() => navigate(`/search?q=${encodeURIComponent(suggestion)}`)}
                  className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
          
          {/* Browse Categories */}
          <div className="mt-6">
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse All Products
            </button>
          </div>
        </div>
      )}
      
      <BottomNav />
    </div>
  );
};

export default SearchResultsPage;